import React, { useEffect, useState } from 'react';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../ui/alert-dialog';
import { CheckCircle2, WifiOff, Zap, Send, FileText, Package } from 'lucide-react';
import { useConnectivityStatus } from '../../hooks/useConnectivityStatus';
import { useCompany } from '../../context/CompanyContext';
import { auth } from '../../firebase';

// Canonical completion safeguard — every UI entry point that triggers
// completeJob() routes through this dialog. Completion bills immediately
// (invoice / card-on-file) and advances the recurrence engine, so it must
// never fire on an accidental tap.
//
// The dialog fetches the AUTHORITATIVE completion preview from the
// backend (/api/jobs/{taskId}/completion-preview) so the operator sees
// exactly what will happen — a Visa card charge, an SMS payment link,
// or an unpaid manual invoice — before confirming. The preview uses
// the same billing-policy resolver as complete_job, so the dialog copy
// cannot drift from the actual billing decision.
export const ConfirmCompleteDialog = ({
  open,
  onOpenChange,
  clientName,
  taskId,
  onConfirm,
}) => {
  const { isOffline } = useConnectivityStatus();
  const { companyId } = useCompany();

  const [preview, setPreview]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [previewErr, setError]  = useState(null);

  useEffect(() => {
    if (!open || !taskId || !companyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setPreview(null);
      try {
        const token = await auth.currentUser?.getIdToken(false);
        const url =
          `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${taskId}/completion-preview` +
          `?companyId=${encodeURIComponent(companyId)}`;
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data?.success) {
          setError(data?.detail || `HTTP ${res.status}`);
        } else {
          setPreview(data.preview || null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Preview unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, taskId, companyId]);

  const actionMeta = React.useMemo(() => {
    if (!preview) return null;
    switch (preview.action) {
      case 'auto_charge':
        return { Icon: Zap, tone: 'text-emerald-700 bg-emerald-50 border-emerald-200' };
      case 'invoice_auto_send':
        return { Icon: Send, tone: 'text-blue-700 bg-blue-50 border-blue-200' };
      case 'invoice_manual':
        return { Icon: FileText, tone: 'text-slate-700 bg-slate-50 border-slate-200' };
      case 'prepay_covered':
        return { Icon: Package, tone: 'text-orange-700 bg-orange-50 border-orange-200' };
      case 'no_invoice':
      default:
        return { Icon: FileText, tone: 'text-gray-700 bg-gray-50 border-gray-200' };
    }
  }, [preview]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-sm rounded-2xl" data-testid="confirm-complete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Complete this job?</AlertDialogTitle>
          <AlertDialogDescription data-testid="confirm-complete-description">
            {clientName ? `Finishes the visit for ${clientName}. ` : ''}
            The next recurring visit is scheduled and this can&apos;t be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Backend-driven action preview — single source of truth */}
        {loading && (
          <div
            className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500"
            data-testid="confirm-complete-preview-loading"
          >
            Loading billing preview…
          </div>
        )}
        {previewErr && !loading && (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="confirm-complete-preview-error"
          >
            Could not load billing preview — completion will proceed using the customer&apos;s
            saved billing policy.
          </div>
        )}
        {preview && actionMeta && !loading && (
          <div
            className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${actionMeta.tone}`}
            data-testid="confirm-complete-preview"
            data-preview-action={preview.action}
          >
            <actionMeta.Icon className="h-4 w-4 shrink-0 mt-0.5" />
            <span className="leading-snug">{preview.message}</span>
          </div>
        )}

        {isOffline && (
          <div
            className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
            data-testid="confirm-complete-offline-warning"
          >
            <WifiOff className="h-4 w-4 shrink-0 mt-0.5" />
            <span className="leading-snug">
              You&apos;re offline right now. Completing this job requires a connection —
              try again once your phone reconnects.
            </span>
          </div>
        )}
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel data-testid="confirm-complete-cancel" className="h-11">
            Not yet
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-complete-confirm"
            className="h-11 bg-emerald-600 hover:bg-emerald-700"
            onClick={onConfirm}
          >
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            Complete Job
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmCompleteDialog;
